import { useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { NavLink } from "react-router-dom"
import { AlertTriangle, Copy, Download, KeyRound, MoreHorizontal, Pencil, Plus, Search } from "lucide-react"
import { UsernameSchema, type SystemRole } from "@pishgam/contracts"

import { ApiFailure, api } from "../api"
import { ConfirmDialog, DataCard, EmptyState, LoadingCard, ProfilePictureUpload, messageFor, roleLabel } from "../shared"
import { cn } from "@/lib/utils"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { TableCell, TableRow } from "@/components/ui/table"

type AdminUser = { id: number; username: string; nameFa: string; email: string; role: string; roles: SystemRole[]; divisionId: number | null; isActive: boolean; isSuperuser: boolean; profileImageUrl?: string; division?: { id: number; nameFa: string } | null; headingDivisions: Array<{ id: number; nameFa: string }>; headingBusinessUnits: Array<{ id: number; nameFa: string }> }

const SYSTEM_ROLES: Array<{ value: SystemRole; label: string; hint: string }> = [
  { value: "cfo", label: "مدیر مالی", hint: "حقوق و دستمزد، ورود داده‌های مالی، پایش بستن ماه و همه گزارش‌ها" },
  { value: "board", label: "هیئت مدیره", hint: "گزارش نهایی سود و زیان هر کسب‌وکار (بدون جزئیات حقوق)" },
  { value: "admin", label: "مدیر سامانه", hint: "همه بخش‌ها، کاربران، سازمان و تنظیمات" },
]
type Division = { id: number; nameFa: string; isCostBucket?: boolean; headId?: number | null; head?: { id: number; nameFa: string } | null }
type Business = { id: number; nameFa: string; headId?: number | null; head?: { id: number; nameFa: string } | null }
type Org = { divisions: Division[]; businessUnits: Business[] }
type Credential = { nameFa: string; username: string; password: string }

const ROLES = ["employee", "division_head", "bu_head", "cfo", "admin"] as const

export function AdminUsers() {
  const client = useQueryClient()
  const [q, setQ] = useState("")
  const users = useQuery({ queryKey: ["admin-users"], queryFn: () => api<AdminUser[]>("/admin/users") })
  const divisions = useQuery({ queryKey: ["organization"], queryFn: () => api<Org>("/organization") })
  const [editing, setEditing] = useState<AdminUser | "new" | null>(null)
  const [credential, setCredential] = useState<Credential | null>(null)
  const [resetTarget, setResetTarget] = useState<AdminUser | null>(null)
  const reset = useMutation({
    mutationFn: (user: AdminUser) => api<{ username: string; temporaryPassword: string }>(`/admin/users/${user.id}/reset-password`, { method: "POST" }).then((data) => ({ nameFa: user.nameFa, username: data.username, password: data.temporaryPassword })),
    onSuccess: (value) => { setResetTarget(null); setCredential(value) },
  })
  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase()
    return (users.data ?? []).filter((user) => !needle || user.nameFa.includes(needle) || user.username.toLowerCase().includes(needle))
  }, [users.data, q])

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-72">
          <Search className="pointer-events-none absolute top-1/2 right-3 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(event) => setQ(event.target.value)} placeholder="جست‌وجوی نام یا نام کاربری" className="ps-9" aria-label="جستجوی کاربر" />
        </div>
        <div className="ms-auto flex flex-wrap gap-2">
          <Button variant="outline" asChild><NavLink to="/admin/reissue"><KeyRound className="size-4" /> صدور مجدد اطلاعات ورود</NavLink></Button>
          <Button onClick={() => setEditing("new")}><Plus className="size-4" /> افزودن کاربر</Button>
        </div>
      </div>
      {users.isLoading ? <LoadingCard /> : (
        <DataCard headers={["نام", "نام کاربری", "نقش‌ها", "واحد", "وضعیت", ""]} empty={q.trim() ? `کاربری با «${q.trim()}» پیدا نشد.` : "هنوز کاربری ثبت نشده است."}>
          {filtered.map((user) => (
            <TableRow key={user.id} className={cn(!user.isActive && "text-muted-foreground")}>
              <TableCell>
                <div className="flex items-center gap-3">
                  <Avatar className="size-8">
                    {user.profileImageUrl && <AvatarImage src={user.profileImageUrl} alt="" />}
                    <AvatarFallback className="text-xs">{user.nameFa?.slice(0, 1) || "U"}</AvatarFallback>
                  </Avatar>
                  <span className="font-medium">{user.nameFa}</span>
                </div>
              </TableCell>
              <TableCell dir="ltr" className="text-right font-mono text-sm text-muted-foreground">{user.username}</TableCell>
              <TableCell><RoleBadges user={user} /></TableCell>
              <TableCell>{user.division?.nameFa ?? "—"}</TableCell>
              <TableCell>
                {user.isActive
                  ? <span className="inline-flex items-center gap-1.5 text-sm"><span aria-hidden className="size-1.5 rounded-full bg-success" />فعال</span>
                  : <Badge variant="secondary">غیرفعال</Badge>}
              </TableCell>
              <TableCell>
                <div className="flex justify-end gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setEditing(user)}><Pencil className="size-4" aria-hidden /> ویرایش</Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon-sm" aria-label={`کارهای دیگر برای ${user.nameFa}`}><MoreHorizontal className="size-4" aria-hidden /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem disabled={reset.isPending} onSelect={() => setResetTarget(user)}><KeyRound className="size-4" aria-hidden /> بازنشانی رمز عبور</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </DataCard>
      )}
      {editing && (
        <UserDialog user={editing === "new" ? null : editing} divisions={divisions.data?.divisions ?? []} businesses={divisions.data?.businessUnits ?? []} onClose={() => setEditing(null)} onPhoto={() => void client.invalidateQueries({ queryKey: ["admin-users"] })}
          onSaved={(created) => { setEditing(null); for (const key of ["admin-users", "organization", "admin-organization", "process-tasks"]) void client.invalidateQueries({ queryKey: [key] }); if (created) setCredential(created) }} />
      )}
      {credential && <CredentialDialog credential={credential} onClose={() => setCredential(null)} />}
      <ConfirmDialog
        open={resetTarget !== null} onOpenChange={(next) => { if (!next) setResetTarget(null) }}
        title={`بازنشانی رمز عبور ${resetTarget?.nameFa ?? ""}`}
        description={`رمز عبور «${resetTarget?.nameFa ?? ""}» بازنشانی شود؟ رمز فعلی دیگر کار نمی‌کند و یک رمز موقت فقط یک بار نمایش داده می‌شود.`}
        confirmLabel="بازنشانی رمز" danger pending={reset.isPending}
        onConfirm={() => { if (resetTarget) reset.mutate(resetTarget) }}
      >
        {reset.error && <p role="alert" className="text-sm text-loss">{messageFor(reset.error)}</p>}
      </ConfirmDialog>
    </>
  )
}

function UserDialog({ user, divisions, businesses, onClose, onSaved, onPhoto }: { user: AdminUser | null; divisions: Division[]; businesses: Business[]; onClose: () => void; onSaved: (created: Credential | null) => void; onPhoto?: () => void }) {
  const [heads, setHeads] = useState({ divisions: user?.headingDivisions.map((item) => item.id) ?? [], businesses: user?.headingBusinessUnits.map((item) => item.id) ?? [] })
  const toggleHead = (kind: "divisions" | "businesses", id: number, on: boolean) => setHeads((current) => ({ ...current, [kind]: on ? [...current[kind], id] : current[kind].filter((item) => item !== id) }))
  const [form, setForm] = useState({ nameFa: user?.nameFa ?? "", username: user?.username ?? "", roles: user?.roles ?? ([] as SystemRole[]), divisionId: user?.divisionId ? String(user.divisionId) : "none", isActive: user?.isActive ?? true, password: "" })
  const usernameCheck = UsernameSchema.safeParse(form.username)
  const passwordOk = !form.password || form.password.length >= 8
  const mutation = useMutation({
    mutationFn: async () => {
      const body = { nameFa: form.nameFa, username: form.username.trim().toLowerCase(), roles: form.roles, divisionId: form.divisionId === "none" ? null : Number(form.divisionId), isActive: form.isActive, email: user?.email ?? "", headDivisionIds: heads.divisions, headBusinessUnitIds: heads.businesses }
      if (user) { await api(`/admin/users/${user.id}`, { method: "PATCH", body: JSON.stringify(body) }); return null }
      const result = await api<{ user: { username: string }; temporaryPassword: string }>("/admin/users", { method: "POST", body: JSON.stringify({ ...body, password: form.password }) })
      return { nameFa: form.nameFa, username: result.user.username, password: result.temporaryPassword }
    },
    onSuccess: onSaved,
  })
  const set = (key: keyof typeof form) => (value: string | boolean) => setForm((current) => ({ ...current, [key]: value }))
  const toggleRole = (role: SystemRole, on: boolean) => setForm((current) => ({ ...current, roles: on ? [...current.roles, role] : current.roles.filter((item) => item !== role) }))

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{user ? "ویرایش کاربر" : "افزودن کاربر"}</DialogTitle>
          <DialogDescription>{user ? "تغییر نام کاربری یا غیرفعال کردن، کاربر را از سامانه خارج می‌کند." : "کاربر در اولین ورود باید رمز عبور خود را تغییر دهد."}</DialogDescription>
        </DialogHeader>
        <form onSubmit={(event) => { event.preventDefault(); mutation.mutate() }}>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor="user-name">نام و نام خانوادگی</FieldLabel>
              <Input id="user-name" value={form.nameFa} onChange={(event) => set("nameFa")(event.target.value)} required autoFocus />
            </Field>
            <Field>
              <FieldLabel htmlFor="user-username">نام کاربری</FieldLabel>
              <Input id="user-username" dir="ltr" className="text-left font-mono" value={form.username} onChange={(event) => set("username")(event.target.value)} placeholder="m.rezaei" required aria-invalid={Boolean(form.username) && !usernameCheck.success} />
              <FieldDescription>حروف کوچک انگلیسی، مثل حرف اول نام + نقطه + نام خانوادگی.</FieldDescription>
              {form.username && !usernameCheck.success && <FieldError>{usernameCheck.error.issues[0]?.message}</FieldError>}
            </Field>
            <fieldset className="grid gap-2 rounded-lg border p-3">
              <legend className="px-1 text-sm font-medium">نقش‌ها</legend>
              <p className="text-xs text-muted-foreground">هر کس به‌طور پیش‌فرض «پرسنل» است و نسبت کاری ماهانه ثبت می‌کند. مدیریت واحدها و کسب‌وکارها را پایین‌تر تیک بزنید.</p>
              {SYSTEM_ROLES.map((role) => (
                <label key={role.value} className="flex items-start gap-2 text-sm">
                  <input type="checkbox" className="mt-0.5 size-4 accent-primary" checked={form.roles.includes(role.value)} disabled={role.value === "admin" && user?.isSuperuser} onChange={(event) => toggleRole(role.value, event.target.checked)} />
                  <span><span className="font-medium">{role.label}</span> <span className="text-muted-foreground">— {role.hint}</span></span>
                </label>
              ))}
            </fieldset>
            <HeadPicker legend="مدیر واحد" hint="نسبت کاری پرسنل این واحدها را تأیید می‌کند." items={divisions.filter((item) => !item.isCostBucket)} selected={heads.divisions} userId={user?.id} onToggle={(id, on) => toggleHead("divisions", id, on)} />
            <HeadPicker legend="مدیر کسب‌وکار" hint="سهم این کسب‌وکارها را تأیید نهایی و پیش‌بینی آن‌ها را به‌روز می‌کند." items={businesses} selected={heads.businesses} userId={user?.id} onToggle={(id, on) => toggleHead("businesses", id, on)} />
            <div className="grid gap-4">
              <Field>
                <FieldLabel>واحد</FieldLabel>
                <Select value={form.divisionId} onValueChange={set("divisionId")}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">بدون واحد</SelectItem>
                    {divisions.map((division) => <SelectItem key={division.id} value={String(division.id)}>{division.nameFa}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            </div>
            {!user && (
              <Field>
                <FieldLabel htmlFor="user-password">رمز عبور اولیه (اختیاری)</FieldLabel>
                <Input id="user-password" dir="ltr" className="text-left font-mono" value={form.password} onChange={(event) => set("password")(event.target.value)} autoComplete="new-password" aria-invalid={!passwordOk} />
                <FieldDescription>خالی بگذارید تا سامانه یک رمز امن بسازد. حداقل ۸ نویسه.</FieldDescription>
              </Field>
            )}
            {user && (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <label className="flex items-center gap-2 text-sm"><input type="checkbox" className="size-4 accent-primary" checked={form.isActive} onChange={(event) => set("isActive")(event.target.checked)} /> حساب فعال است</label>
                <ProfilePictureUpload endpoint={`/uploads/users/${user.id}/profile-picture`} label="تغییر عکس پروفایل" onUploaded={onPhoto} />
              </div>
            )}
            {mutation.error && <FieldError>{messageFor(mutation.error)}</FieldError>}
            <DialogFooter>
              <Button type="submit" disabled={mutation.isPending || !form.nameFa.trim() || !usernameCheck.success || !passwordOk}>{mutation.isPending ? "در حال ذخیره…" : user ? "ذخیره" : "ساخت حساب"}</Button>
              <Button type="button" variant="ghost" onClick={onClose}>انصراف</Button>
            </DialogFooter>
          </FieldGroup>
        </form>
      </DialogContent>
    </Dialog>
  )
}

/** One checkbox per unit/business; shows who heads it now and who gets replaced by ticking it. */
function HeadPicker({ legend, hint, items, selected, userId, onToggle }: {
  legend: string; hint: string; items: Array<{ id: number; nameFa: string; head?: { id: number; nameFa: string } | null }>; selected: number[]; userId?: number; onToggle: (id: number, on: boolean) => void
}) {
  return (
    <fieldset className="grid gap-1.5 rounded-lg border p-3">
      <legend className="px-1 text-sm font-medium">{legend}</legend>
      <p className="mb-1 text-xs text-muted-foreground">{hint}</p>
      <div className="grid gap-1.5 sm:grid-cols-2">
        {items.map((item) => {
          const checked = selected.includes(item.id)
          const other = item.head && item.head.id !== userId ? item.head : null
          return (
            <label key={item.id} className={cn("flex items-start gap-2 rounded-md border px-2.5 py-2 text-sm transition-colors", checked ? "border-primary/50 bg-primary/5" : "border-transparent hover:bg-muted/50")}>
              <input type="checkbox" className="mt-1 size-4 shrink-0 accent-primary" checked={checked} onChange={(event) => onToggle(item.id, event.target.checked)} />
              <span className="min-w-0">
                <span className="block font-medium">{item.nameFa}</span>
                {other ? (
                  <span className={cn("block text-xs", checked ? "font-medium text-amber-700" : "text-muted-foreground")}>{checked ? `جایگزین ${other.nameFa} می‌شود` : `مدیر فعلی: ${other.nameFa}`}</span>
                ) : !checked ? <span className="block text-xs text-muted-foreground">بدون مدیر</span> : null}
              </span>
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}

function RoleBadges({ user }: { user: AdminUser }) {
  const labels = [
    ...user.roles.map((role) => SYSTEM_ROLES.find((item) => item.value === role)?.label ?? role),
    ...user.headingBusinessUnits.map((unit) => `مدیر ${unit.nameFa}`),
    ...user.headingDivisions.map((division) => `مدیر واحد ${division.nameFa}`),
  ]
  if (user.isSuperuser && !user.roles.includes("admin")) labels.unshift("مدیر سامانه")
  return (
    <div className="flex max-w-72 flex-wrap gap-1">
      {labels.length ? labels.map((label) => <Badge key={label} variant="secondary" className="font-normal">{label}</Badge>) : <span className="text-sm text-muted-foreground">پرسنل</span>}
    </div>
  )
}

function CredentialDialog({ credential, onClose }: { credential: Credential; onClose: () => void }) {
  const [copied, setCopied] = useState(false)
  const text = `${credential.nameFa}\nنام کاربری: ${credential.username}\nرمز عبور: ${credential.password}`
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose() }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>اطلاعات ورود {credential.nameFa}</DialogTitle>
          <DialogDescription>این رمز فقط همین یک بار نمایش داده می‌شود. کاربر در اولین ورود باید آن را تغییر دهد.</DialogDescription>
        </DialogHeader>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 rounded-lg bg-muted p-4">
          <dt className="text-sm text-muted-foreground">نام کاربری</dt><dd dir="ltr" className="text-left font-mono">{credential.username}</dd>
          <dt className="text-sm text-muted-foreground">رمز عبور</dt><dd dir="ltr" className="text-left font-mono text-lg">{credential.password}</dd>
        </dl>
        <DialogFooter>
          <Button onClick={() => { void navigator.clipboard.writeText(text).then(() => setCopied(true)) }}><Copy className="size-4" /> {copied ? "کپی شد" : "کپی"}</Button>
          <Button variant="ghost" onClick={onClose}>بستن</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ─── bulk reissue ───────────────────────────────────────────────────────────

type Proposal = { id: number; currentUsername: string; proposed: string; needsReview: boolean; nameFa: string; role: string; division: string }

export function ReissueCredentials() {
  const client = useQueryClient()
  const preview = useQuery({ queryKey: ["reissue-preview"], queryFn: () => api<Proposal[]>("/admin/users/reissue-preview"), staleTime: Infinity })
  const [names, setNames] = useState<Record<number, string>>({})
  const [confirmed, setConfirmed] = useState(false)
  const [done, setDone] = useState<number | null>(null)
  const rows = preview.data ?? []
  const nameOf = (row: Proposal) => (names[row.id] ?? row.proposed).trim().toLowerCase()
  const counts = rows.reduce<Record<string, number>>((acc, row) => { acc[nameOf(row)] = (acc[nameOf(row)] ?? 0) + 1; return acc }, {})
  const problems = rows.filter((row) => !UsernameSchema.safeParse(nameOf(row)).success || counts[nameOf(row)]! > 1)

  const issue = useMutation({
    mutationFn: async () => {
      const csrf = decodeURIComponent(document.cookie.split("; ").find((part) => part.startsWith("pishgam_csrf="))?.split("=").slice(1).join("=") ?? "")
      const response = await fetch("/api/v1/admin/users/reissue", { method: "POST", credentials: "include", headers: { "Content-Type": "application/json", "X-CSRF-Token": csrf }, body: JSON.stringify({ users: rows.map((row) => ({ id: row.id, username: nameOf(row) })) }) })
      if (!response.ok) { const body = await response.json().catch(() => ({})) as { message?: string }; throw new ApiFailure(body.message ?? "صدور اطلاعات ورود انجام نشد.", response.status) }
      const blob = await response.blob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.download = `user_credentials_${new Date().toISOString().slice(0, 10)}.xlsx`
      link.click()
      setTimeout(() => URL.revokeObjectURL(url), 10_000)
    },
    onSuccess: () => { setDone(rows.length); void client.invalidateQueries({ queryKey: ["admin-users"] }); void client.removeQueries({ queryKey: ["reissue-preview"] }) },
  })

  if (done !== null) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>اطلاعات ورود {done.toLocaleString("fa-IR")} کاربر صادر شد</CardTitle>
          <CardDescription>فایل اکسل دانلود شد. رمزها در سامانه ذخیره نمی‌شوند و دوباره قابل مشاهده نیستند؛ فایل را امن نگه دارید. نام‌های کاربری و رمزهای قبلی دیگر کار نمی‌کنند.</CardDescription>
        </CardHeader>
        <CardContent><Button asChild variant="outline"><NavLink end to="/admin">بازگشت به کاربران</NavLink></Button></CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>صدور مجدد اطلاعات ورود</CardTitle>
          <CardDescription>
            همه کاربران فعال به جز «مدیر سامانه» نام کاربری کوتاه و رمز عبور تازه می‌گیرند و یک فایل اکسل برای تحویل به همکاران دانلود می‌شود.
            سوابق تخصیص و حقوق هر نفر دست‌نخورده می‌ماند. هر کاربر در اولین ورود باید رمز خود را تغییر دهد.
          </CardDescription>
        </CardHeader>
      </Card>
      {preview.isLoading ? <LoadingCard /> : preview.error ? <p className="text-sm text-loss">{messageFor(preview.error)}</p> : !rows.length ? <EmptyState text="کاربری برای صدور مجدد وجود ندارد." /> : (
        <>
          <DataCard headers={["نام", "واحد", "نقش", "نام کاربری فعلی", "نام کاربری جدید"]} empty="">
            {rows.map((row) => {
              const value = names[row.id] ?? row.proposed
              const bad = problems.includes(row)
              return (
                <TableRow key={row.id}>
                  <TableCell className="font-medium">{row.nameFa}</TableCell>
                  <TableCell>{row.division || "—"}</TableCell>
                  <TableCell>{roleLabel[row.role] ?? row.role}</TableCell>
                  <TableCell dir="ltr" className="text-right font-mono text-sm text-muted-foreground">{row.currentUsername}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Input dir="ltr" className="h-8 w-48 text-left font-mono" value={value} aria-label={`نام کاربری جدید ${row.nameFa}`} aria-invalid={bad}
                        onChange={(event) => setNames((current) => ({ ...current, [row.id]: event.target.value }))} />
                      {bad ? <Badge variant="destructive">تکراری/نامعتبر</Badge> : row.needsReview && names[row.id] === undefined ? <Badge variant="outline" className="gap-1"><AlertTriangle className="size-3" /> بررسی شود</Badge> : null}
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </DataCard>
          <Card>
            <CardContent className="flex flex-wrap items-center gap-4 pt-6">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" className="size-4 accent-primary" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />
                می‌دانم که رمز عبور {rows.length.toLocaleString("fa-IR")} کاربر عوض می‌شود و همه از سامانه خارج می‌شوند.
              </label>
              <Button className="ms-auto" disabled={!confirmed || problems.length > 0 || issue.isPending} onClick={() => issue.mutate()}>
                <Download className="size-4" /> {issue.isPending ? "در حال صدور…" : "صدور و دریافت فایل اکسل"}
              </Button>
              {issue.error && <p className="w-full text-sm text-loss">{messageFor(issue.error)}</p>}
            </CardContent>
          </Card>
        </>
      )}
    </>
  )
}
